import {
  AskAIPage,
  ContactsPage,
  DashboardPage,
  InboxConversationView,
  InboxThreadAndConversationPage,
  InboxPhoneCallView,
  InboxSMSCompositionPanel,
  CompleteSSOPage,
  ESignatureTermsPage,
  LoginPage,
  MFAPage,
  MobileAppLoginPage,
  MobileMFAPage,
  MobileAppSuccessPage,
  TeamChatPage,
  TicketsViewsPage,
  InboxChatCompositionPanel,
  InboxEmailCompositionPanel,
  InboxZendeskCompositionPanel,
  InboxDraftsAndSentRedirectPage,
  InboxThreadOnlyPage,
  TicketQueuePage,
  VitalsViewsPage,
  OrganizationSettingsPage,
  PhoneSettingsPage,
  TeamSettingsPage,
  CalendarDateRedirectPage,
  CalendarPage,
  CalendarAvailabilityPage,
  CalendarLocationsPage,
  CalendarBookingPagesPage,
  CalendarTemplatesPage,
  EmailSettingsPage,
  AppoinmentRemindersPage,
  TemplatesPage,
  ContactDetailsTakeoverPage,
  ContactTimelineTakeoverPage,
} from "../pages";
import type { ROUTES_CONFIG } from "./routes-config";
import { RouterComponentMap } from "./types";
import { TicketDetailsPage } from "../pages/tickets/TicketDetailsPage";
import {
  ContactDetailsTakeoverCard,
  ContactTimelineTakeoverCard,
  NavbarTeamChatNavItem,
} from "../components/AppNavbar";
import { PreferencesSettingsPage } from "../pages/settings/OtherSettings/PreferencesSettingsPage";

const INBOX_COMPONENTS_MAP = {
  "/email": {
    subRoutes: {
      "/new": {
        pageComponent: <InboxEmailCompositionPanel />,
        useDefaultTitle: false,
      },
      "/:threadId": {
        pageComponent: <InboxConversationView />,
        useDefaultTitle: false,
      },
    },
    useDefaultTitle: false,
  },
  "/groupmms": {
    subRoutes: {
      "/:threadId": {
        pageComponent: <InboxConversationView />,
        useDefaultTitle: false,
      },
    },
    useDefaultTitle: false,
  },
  "/sms": {
    subRoutes: {
      "/new": {
        pageComponent: <InboxSMSCompositionPanel />,
        useDefaultTitle: false,
      },
      "/:threadId": {
        pageComponent: <InboxConversationView />,
        useDefaultTitle: false,
      },
    },
    useDefaultTitle: false,
  },
  "/chat": {
    subRoutes: {
      "/new": {
        pageComponent: <InboxChatCompositionPanel />,
        useDefaultTitle: false,
      },
      "/:threadId": {
        pageComponent: <InboxConversationView />,
        useDefaultTitle: false,
      },
    },
    useDefaultTitle: false,
  },
  "/phone": {
    subRoutes: {
      "/:threadId": {
        pageComponent: <InboxPhoneCallView />,
        useDefaultTitle: false,
      },
    },
    useDefaultTitle: false,
  },
  "/zendesk": {
    subRoutes: {
      "/new": {
        pageComponent: <InboxZendeskCompositionPanel />,
        useDefaultTitle: false,
      },
      "/:threadId": {
        pageComponent: <InboxConversationView />,
        useDefaultTitle: false,
      },
    },
    useDefaultTitle: false,
  },
} as const satisfies RouterComponentMap<(typeof ROUTES_CONFIG)["/messaging"]["subRoutes"]["/inbox"]["subRoutes"]>;

export const ROUTER_COMPONENTS_MAP = {
  "/login": {
    pageComponent: <LoginPage />,
  },
  "/mfa": {
    pageComponent: <MFAPage />,
  },
  "/complete-sso": {
    pageComponent: <CompleteSSOPage />,
  },
  "/e-signature-terms": {
    pageComponent: <ESignatureTermsPage />,
  },
  "/mobile-app": {
    subRoutes: {
      "/login": {
        pageComponent: <MobileAppLoginPage />,
      },
      "/mfa": {
        pageComponent: <MobileMFAPage />,
      },
      "/success": {
        pageComponent: <MobileAppSuccessPage />,
      },
    },
  },
  "/dashboard": {
    pageComponent: <DashboardPage />,
  },
  "/ask-ai": {
    pageComponent: <AskAIPage />,
    useDefaultTitle: false,
  },
  "/contacts": {
    indexRedirect: "/contacts/views/all",
    subRoutes: {
      "/views": {
        indexRedirect: "/contacts/views/all",
        subRoutes: {
          "/:viewId": {
            pageComponent: <ContactsPage />,
          },
        },
      },
      "/timeline": {
        subRoutes: {
          "/:enduserId": {
            pageComponent: <ContactTimelineTakeoverPage />,
            useDefaultTitle: false,
            takeoverCardComponent: <ContactTimelineTakeoverCard />,
          },
        },
      },
    },
  },
  "/contact-details": {
    subRoutes: {
      "/:enduserId": {
        subRoutes: {
          "/:viewId": {
            subRoutes: {
              "/:blockId": {
                pageComponent: <ContactDetailsTakeoverPage />,
                useDefaultTitle: false,
                takeoverCardComponent: <ContactDetailsTakeoverCard />,
              },
            },
          },
        },
      },
    },
  },
  "/tickets": {
    useDefaultTitle: false,
    indexRedirect: "/tickets/views/default",
    subRoutes: {
      "/views": {
        useDefaultTitle: false,
        indexRedirect: "/tickets/views/default",
        subRoutes: {
          "/:viewId": {
            pageComponent: <TicketsViewsPage />,
            useDefaultTitle: false,
          },
        },
      },
      "/queues": {
        useDefaultTitle: false,
        indexRedirect: "/tickets/queues/default",
        subRoutes: {
          "/:queueId": {
            pageComponent: <TicketQueuePage />,
            useDefaultTitle: false,
          },
        },
      },
      "/details": {
        subRoutes: {
          "/:ticketId": {
            pageComponent: <TicketDetailsPage />,
            useDefaultTitle: false,
          },
        },
        useDefaultTitle: false,
      },
    },
  },
  "/templates": {
    indexRedirect: "/templates/views/all",
    subRoutes: {
      "/views": {
        useDefaultTitle: false,
        indexRedirect: "/templates/views/all",
        subRoutes: {
          "/:viewId": {
            useDefaultTitle: false,
            pageComponent: <TemplatesPage />,
          },
        },
      },
      // "/snippets": {
      //   useDefaultTitle: false,
      //   pageComponent: <div>snippets</div>,
      // },
    },
  },
  "/vitals": {
    indexRedirect: "/vitals/views/all",
    subRoutes: {
      "/views": {
        useDefaultTitle: false,
        indexRedirect: "/vitals/views/all",
        subRoutes: {
          "/:viewId": {
            pageComponent: <VitalsViewsPage />,
            useDefaultTitle: false,
          },
        },
      },
    },
  },
  "/settings": {
    useDefaultTitle: false,
    subRoutes: {
      "/organizations": {
        pageComponent: <OrganizationSettingsPage />,
        useDefaultTitle: false,
      },
      "/email": {
        pageComponent: <EmailSettingsPage />,
        useDefaultTitle: false,
      },
      "/phone": {
        indexRedirect: "/settings/phone/numbers",
        useDefaultTitle: false,
        subRoutes: {
          "/numbers": { useDefaultTitle: false, pageComponent: <PhoneSettingsPage selectedTab="numbers" /> },
          "/usage": { useDefaultTitle: false, pageComponent: <PhoneSettingsPage selectedTab="usage" /> },
          "/logs": {
            useDefaultTitle: false,
            indexRedirect: "/settings/phone/logs/views/all",
            subRoutes: {
              "/views": {
                useDefaultTitle: false,
                indexRedirect: "/settings/phone/logs/views/all",
                subRoutes: {
                  "/:viewId": { useDefaultTitle: false, pageComponent: <PhoneSettingsPage selectedTab="logs" /> },
                },
              },
            },
          },
        },
      },
      "/team": {
        indexRedirect: "/settings/team/views/all",
        useDefaultTitle: false,
        subRoutes: {
          "/views": {
            useDefaultTitle: false,
            indexRedirect: "/settings/team/views/all",
            subRoutes: {
              "/:viewId": { useDefaultTitle: false, pageComponent: <TeamSettingsPage selectedTab="team" /> },
            },
          },
          "/roles": { useDefaultTitle: false, pageComponent: <TeamSettingsPage selectedTab="roles" /> },
          "/configuration": {
            useDefaultTitle: false,
            pageComponent: <TeamSettingsPage selectedTab="configuration" />,
          },
        },
      },
      "/other": {
        indexRedirect: "/settings/other/preferences",
        subRoutes: {
          "/preferences": {
            pageComponent: <PreferencesSettingsPage />,
            useDefaultTitle: false,
          },
        },
      },
    },
  },
  "/messaging": {
    subRoutes: {
      "/inbox": {
        pageComponent: <InboxThreadAndConversationPage />,
        subRoutes: INBOX_COMPONENTS_MAP,
        useDefaultTitle: false,
      },
      "/drafts-sent": {
        pageComponent: <InboxDraftsAndSentRedirectPage />,
        subRoutes: {
          "/:view": {
            pageComponent: <InboxThreadOnlyPage />,
            useDefaultTitle: false,
          },
        },
        useDefaultTitle: false,
      },
      "/archive": {
        pageComponent: <InboxThreadAndConversationPage />,
        subRoutes: INBOX_COMPONENTS_MAP,
        useDefaultTitle: false,
      },
      "/trash": {
        pageComponent: <InboxThreadAndConversationPage />,
        subRoutes: INBOX_COMPONENTS_MAP,
        useDefaultTitle: false,
      },
      "/team-chat": {
        subRoutes: {
          "/:chatroomId": {
            pageComponent: <TeamChatPage />,
            useDefaultTitle: false,
          },
        },
        customSidebarComponent: <NavbarTeamChatNavItem />,
      },
    },
  },
  "/calendar": {
    indexRedirect: <CalendarDateRedirectPage date="today" />,
    useDefaultTitle: false,
    subRoutes: {
      "/availability": {
        pageComponent: <CalendarAvailabilityPage />,
        useDefaultTitle: false,
      },
      "/locations": {
        pageComponent: <CalendarLocationsPage />,
        useDefaultTitle: false,
      },
      "/templates": {
        pageComponent: <CalendarTemplatesPage />,
        useDefaultTitle: false,
      },
      "/booking-pages": {
        pageComponent: <CalendarBookingPagesPage />,
        useDefaultTitle: false,
      },
      "/view": {
        indexRedirect: <CalendarDateRedirectPage date="today" />,
        useDefaultTitle: false,
        subRoutes: {
          "/:viewId": {
            indexRedirect: <CalendarDateRedirectPage date="today" />,
            useDefaultTitle: false,
            subRoutes: {
              "/month": {
                indexRedirect: <CalendarDateRedirectPage viewType="month" date="today" />,
                useDefaultTitle: false,
                subRoutes: {
                  "/:year": {
                    indexRedirect: <CalendarDateRedirectPage viewType="month" date="today" />,
                    useDefaultTitle: false,
                    subRoutes: {
                      "/:month": {
                        pageComponent: <CalendarPage />,
                        useDefaultTitle: false,
                      },
                    },
                  },
                },
              },
              "/year": {
                indexRedirect: <CalendarDateRedirectPage viewType="year" date="today" />,
                useDefaultTitle: false,
                subRoutes: {
                  "/:year": {
                    pageComponent: <CalendarPage />,
                    useDefaultTitle: false,
                  },
                },
              },
              "/day": {
                indexRedirect: <CalendarDateRedirectPage viewType="day" date="today" />,
                useDefaultTitle: false,
                subRoutes: {
                  "/:year": {
                    indexRedirect: <CalendarDateRedirectPage viewType="day" date="today" />,
                    useDefaultTitle: false,
                    subRoutes: {
                      "/:month": {
                        indexRedirect: <CalendarDateRedirectPage viewType="day" date="today" />,
                        useDefaultTitle: false,
                        subRoutes: {
                          "/:day": {
                            pageComponent: <CalendarPage />,
                            useDefaultTitle: false,
                          },
                        },
                      },
                    },
                  },
                },
              },
              "/list": {
                indexRedirect: <CalendarDateRedirectPage viewType="list" date="today" />,
                useDefaultTitle: false,
                subRoutes: {
                  "/:year": {
                    indexRedirect: <CalendarDateRedirectPage viewType="list" date="today" />,
                    useDefaultTitle: false,
                    subRoutes: {
                      "/:month": {
                        pageComponent: <CalendarPage />,
                        useDefaultTitle: false,
                      },
                    },
                  },
                },
              },
              "/week": {
                indexRedirect: <CalendarDateRedirectPage viewType="week" date="today" />,
                useDefaultTitle: false,
                subRoutes: {
                  "/:year": {
                    indexRedirect: <CalendarDateRedirectPage viewType="week" date="today" />,
                    useDefaultTitle: false,
                    subRoutes: {
                      "/:month": {
                        indexRedirect: <CalendarDateRedirectPage viewType="week" date="today" />,
                        useDefaultTitle: false,
                        subRoutes: {
                          "/:day": {
                            pageComponent: <CalendarPage />,
                            useDefaultTitle: false,
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      },
    },
  },
  "/appointment-reminders": {
    pageComponent: <AppoinmentRemindersPage />,
    useDefaultTitle: false,
  },
} as const satisfies RouterComponentMap<typeof ROUTES_CONFIG>;
